import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import { Button, Menu, MenuItem } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { useLocation, useNavigate, useSearch } from '@tanstack/react-router';
import type React from 'react';
import { type MouseEvent, useState } from 'react';
import { GALLERY_ONLY, GRID, HOME_PATH } from 'src/constants';
import { persistLayout } from 'src/helpers/layoutPersistence';
import { akomiMediaCardGridVar } from 'src/store/akomiMediaCardGridVar';
import type { BaseSearchParams } from 'src/utils/searchSchemas';

const useStyles = makeStyles((theme: Theme) => ({
  scopeButton: {
    color: theme.palette.secondary.main,
    borderRight: `1px solid ${theme.palette.divider}`,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1),
    textTransform: 'none',
    fontSize: '14px',
    fontWeight: 500,
    minWidth: '100px',
    '&:hover': {
      backgroundColor: `${theme.palette.secondary.light}1A`,
    },
  },
  menuItem: {
    fontSize: '14px',
  },
  menuItemActive: {
    backgroundColor: `${theme.palette.secondary.light}33`,
  },
}));

type ScopeType = 'all' | 'assets' | 'galleries';

const ScopeSelector: React.FC = () => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  // Get typed search params from TanStack Router - this is the source of truth
  const search = useSearch({ strict: false }) as BaseSearchParams;

  const isGalleryPage = location.pathname.includes(`${HOME_PATH}/gallery/`);

  // Use typed search params instead of window.location.search
  const scope = (search.scope || 'all') as ScopeType;

  const handleClick = (event: MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (): void => {
    setAnchorEl(null);
  };

  const handleSelect = (newScope: ScopeType): void => {
    const currentLayout = akomiMediaCardGridVar().layout;

    // Build the search params update based on scope selection
    const searchUpdates: Partial<BaseSearchParams> = { page: 1 };

    if (newScope === 'all') {
      searchUpdates.scope = undefined;
      if (currentLayout === GALLERY_ONLY) {
        searchUpdates.layout = GRID;
        akomiMediaCardGridVar({
          ...akomiMediaCardGridVar(),
          layout: GRID,
        });
        persistLayout('library', GRID);
      }
    } else if (newScope === 'galleries') {
      searchUpdates.scope = newScope;
      searchUpdates.layout = GALLERY_ONLY;
      akomiMediaCardGridVar({
        ...akomiMediaCardGridVar(),
        layout: GALLERY_ONLY,
      });
      persistLayout('library', GALLERY_ONLY);
    } else {
      searchUpdates.scope = newScope;
      if (currentLayout === GALLERY_ONLY) {
        searchUpdates.layout = GRID;
        akomiMediaCardGridVar({
          ...akomiMediaCardGridVar(),
          layout: GRID,
        });
        persistLayout('library', GRID);
      }
    }

    // Update URL via TanStack Router navigate
    navigate({
      search: ((prev: Record<string, unknown>) => ({
        ...prev,
        ...searchUpdates,
        // biome-ignore lint/suspicious/noExplicitAny: TanStack Router navigate requires type cast for dynamic search params
      })) as any,
    });
    handleClose();
  };

  const labels: Record<ScopeType, string> = {
    all: 'All',
    assets: 'Assets',
    galleries: 'Galleries',
  };

  if (isGalleryPage) {
    return null;
  }

  return (
    <>
      <Button
        onClick={handleClick}
        startIcon={<ViewModuleIcon />}
        endIcon={<ArrowDropDownIcon />}
        className={classes.scopeButton}
        disableRipple
      >
        {labels[scope]}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <MenuItem
          onClick={() => handleSelect('all')}
          className={`${classes.menuItem} ${scope === 'all' ? classes.menuItemActive : ''}`}
        >
          All
        </MenuItem>
        <MenuItem
          onClick={() => handleSelect('assets')}
          className={`${classes.menuItem} ${scope === 'assets' ? classes.menuItemActive : ''}`}
        >
          Assets
        </MenuItem>
        <MenuItem
          onClick={() => handleSelect('galleries')}
          className={`${classes.menuItem} ${scope === 'galleries' ? classes.menuItemActive : ''}`}
        >
          Galleries
        </MenuItem>
      </Menu>
    </>
  );
};

export default ScopeSelector;
